import { useAppStore } from '@/stores/portal-stores/general-stores/app'
import { useLocalizationStore } from '@/stores/portal-stores/general-stores/localization'
import { ErrorCodes, type LogonResponse } from '../../../../generated-api/models/'
import router from '@/router'
import { factory } from '../../../../factory-api/factory'
import { defineStore } from 'pinia'
import { validateCivilRegistrationNumber } from '../../../application/helpers/validateCivilRegistrationNumber'
import { getWebAppUrl, openUrl } from '../../../application/helpers/urlHelper'
import { Capacitor } from '@capacitor/core'

export const useIdLogonStore = defineStore('idLogon', () => {
  const localizationStore = useLocalizationStore()
  const appStore = useAppStore()

  const showErrorMessage = ref<boolean>(false)
  const showCprInput = ref<boolean>(false)
  const buttonSpinner = ref<boolean>(false)
  const showLoginInput = ref<boolean>(false)
  const pageSpinner = ref<boolean>(false)

  const errorMessage = ref<string>('')
  const transactionId = ref<string>('')

  function resetIdLogonStore() {
    buttonSpinner.value = false
    showErrorMessage.value = false
    errorMessage.value = ''
    showCprInput.value = false
    showLoginInput.value = false
    pageSpinner.value = false
  }

  const createTransaction = async () => {
    try {
      buttonSpinner.value = true
      let url = ''

      url = getWebAppUrl()

      let returnUrl = ''

      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        returnUrl =
          import.meta.env.VITE_EID_URL +
          '/id-logon-redirect?url=' +
          url +
          '/id-logon&buttonText=' +
          localizationStore.translate('general_Continue')
      } else {
        returnUrl = url + '/id-logon'
      }
      const idLogonApi = await factory.getIdLogonApi()
      const response = await idLogonApi.createTransaction({
        idLogonCreateTransactionRequest: {
          provider: 'MitId',
          returnUrl: returnUrl
        }
      })

      if (response !== null && response.accessUrl !== undefined) {
        //Open IdLogon window
        openUrl(response.accessUrl, false, '_self')
      } else {
        errorMessage.value = localizationStore.translate('general_Error')
        showErrorMessage.value = true
        return
      }
    } catch (error) {
      console.log(error)
    }
  }

  const logonByTransaction = async () => {
    try {
      pageSpinner.value = true
      buttonSpinner.value = false
      const idLogonApi = await factory.getIdLogonApi()
      const response = await idLogonApi.logonByTransaction({
        idLogonByTransactionIdRequest: { transactionId: transactionId.value }
      })

      if (response.success) {
        redirectAfterSuccess(response)
      } else if (response.success === false) {
        if (response.errorCode === ErrorCodes.CandidateNotFoundByMitIdUserId) {
          //Show CPR input for user to enter CPR
          showCprInput.value = true
        } else {
          //Show generic error message
          errorMessage.value = localizationStore.translate('general_Error')
          showErrorMessage.value = true
        }
        pageSpinner.value = false
      }
    } catch (error) {
      pageSpinner.value = false
      console.log(error)
    }
  }

  const logonByCpr = async (civilRegistrationNumber: string) => {
    try {
      buttonSpinner.value = true

      const cpr = civilRegistrationNumber.slice(0, 6) + '-' + civilRegistrationNumber.slice(6)

      if (validateCivilRegistrationNumber(cpr) === false) {
        errorMessage.value = localizationStore.translate('mitId_Not_Valid_CPR_Format')
        showErrorMessage.value = true
        buttonSpinner.value = false
        return
      }
      const idLogonApi = await factory.getIdLogonApi()
      const response = await idLogonApi.logonByCpr({
        idLogonByCprRequest: { transactionId: transactionId.value, cpr: cpr }
      })

      if (response.success) {
        redirectAfterSuccess(response)
      } else {
        if (response.errorCode === ErrorCodes.CprNotMatchingTransaction) {
          //Ask user to enter cpr again and show error that tells user that CPR is wrong
          errorMessage.value = localizationStore.translate(
            'mitId_Not_Matching_CPR_Transaction_Error'
          )
          showErrorMessage.value = true
          showCprInput.value = true
        } else if (response.errorCode === ErrorCodes.TransactionExpired) {
          //Show error message for expired transaction
          errorMessage.value = localizationStore.translate('mitId_Transaction_Expired_Error')
          showErrorMessage.value = true
        } else if (response.errorCode === ErrorCodes.CandidateNotFoundByCpr) {
          //Show Error message saying that cpr was not found by our system, user should now enter username and password
          showErrorMessage.value = false
          errorMessage.value = ''
          showCprInput.value = false
          showLoginInput.value = true
        } else {
          //Show generic error message
          errorMessage.value = localizationStore.translate('general_Error')
          showErrorMessage.value = true
        }

        buttonSpinner.value = false
      }
    } catch (error) {
      buttonSpinner.value = false
      console.log(error)
    }
  }

  const logonByUsernamePassword = async (username: string, password: string) => {
    try {
      showCprInput.value = false
      buttonSpinner.value = true
      const idLogonApi = await factory.getIdLogonApi()
      const response = await idLogonApi.logonByUsernamePassword({
        idLogonByUsernamePasswordRequest: {
          password: password,
          username: username,
          transactionId: transactionId.value
        }
      })

      if (response.success) {
        redirectAfterSuccess(response)
      } else {
        if (response.errorCode === ErrorCodes.InvalidUsernameOrPassword) {
          //Show error when username or password is invalid
          errorMessage.value = localizationStore.translate('login_InvalidUsernamePassword')
          showLoginInput.value = true
          showErrorMessage.value = true
        } else {
          errorMessage.value = localizationStore.translate('general_Error')
          showErrorMessage.value = true
        }

        buttonSpinner.value = false
      }
    } catch (error) {
      buttonSpinner.value = false
      console.log(error)
    }
  }

  const redirectAfterSuccess = async (response: LogonResponse) => {
    await appStore.getSiteContent()
    if (response.isFirstLogon && response.errorCode === undefined) {
      router.push('/contact-information')
    } else {
      //Frontpage or program selector
      router.push('/')
    }
  }

  const setShowErrorMessage = (visible: boolean) => {
    showErrorMessage.value = visible

    if (visible === false) {
      errorMessage.value = ''
    }
  }

  const setTransactionId = (id: string) => {
    transactionId.value = id
  }

  const setPageSpinner = (visible: boolean) => {
    pageSpinner.value = visible
  }

  const setButtonSpinner = (toggle: boolean) => {
    buttonSpinner.value = toggle
  }

  return {
    createTransaction,
    logonByTransaction,
    showErrorMessage,
    showCprInput,
    buttonSpinner,
    logonByCpr,
    errorMessage,
    setShowErrorMessage,
    showLoginInput,
    logonByUsernamePassword,
    transactionId,
    setTransactionId,
    pageSpinner,
    setPageSpinner,
    setButtonSpinner,
    resetIdLogonStore
  }
})
